import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Grid,
} from '@mui/material';
import { FP } from 'components/financepro';
import { money, formatoFechaLargo, formatoFechaCorto } from './documentoEmpresarial';

const labelSx = {
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: FP.color.textMuted,
  mb: 0.4,
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.7rem',
  color: '#FFFFFF',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: FP.color.navy,
  borderBottom: 'none',
  py: 1.2,
};

/** Encabezado de celdas al estilo del listado de facturas emitidas. */
const cellHeadListadoSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  display: 'block',
  mb: 0.5,
};

const cellBodyEllipsisSx = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const itemsAnchosListado = ['12%', '38%', '10%', '13%', '12%', '15%'];

/**
 * Vista previa empresarial compartida (factura, compra, nota de crédito, cotización).
 */
const DocumentoEmpresarialPreview = ({
  printRef,
  mostrarInfoEmpresa = true,
  nombreEmpresa = 'Empresa',
  direccionEmpresa = '',
  telefonoEmpresa = '',
  rncEmpresa = '',
  emailEmpresa = '',
  tituloDocumento = 'DOCUMENTO',
  numero = '',
  documento = '',
  fecha = '',
  fechaVencimiento = '',
  metaExtra = [],
  parte = null,
  bloqueExtra = null,
  items = [],
  filasTotales = [],
  totalLabel = 'TOTAL',
  totalValue = 0,
  observaciones = '',
  qrDataUrl = '',
  codigoSeguridad = '',
  fechaFirma = '',
  mensajeGracias = '',
  footerNote = '',
  varianteTabla = 'documento',
  ncf_factura_origen = '',
}) => {
  const numeroMostrar = documento && numero ? `${documento}-${numero}` : numero || documento || '—';

  return (
    <Paper
      ref={printRef}
      elevation={0}
      sx={{
        bgcolor: '#FFFFFF',
        border: `1px solid ${FP.color.border}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ height: 4, bgcolor: FP.color.navy }} />
      <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={7}>
            {mostrarInfoEmpresa && (
              <Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: FP.color.navy,
                    fontSize: '1.35rem',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.2,
                    mb: 0.6,
                  }}
                >
                  {nombreEmpresa}
                </Typography>
                {direccionEmpresa && (
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', mb: 0.2 }}>
                    {direccionEmpresa}
                  </Typography>
                )}
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  {telefonoEmpresa && (
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                      Tel: {telefonoEmpresa}
                    </Typography>
                  )}
                  {rncEmpresa && (
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                      RNC: {rncEmpresa}
                    </Typography>
                  )}
                  {emailEmpresa && (
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                      {emailEmpresa}
                    </Typography>
                  )}
                </Stack>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', mt: 0.8 }}>
                  Fecha: {formatoFechaLargo(fecha)}
                </Typography>
              </Box>
            )}
            {!mostrarInfoEmpresa && (
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                Fecha: {formatoFechaLargo(fecha)}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={5}>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  color: FP.color.navy,
                  letterSpacing: '0.1em',
                  fontSize: '0.9rem',
                  mb: 0.4,
                  textTransform: 'uppercase',
                }}
              >
                {tituloDocumento}
              </Typography>
              {(metaExtra || [])
                .filter((m) => m && m.value)
                .map((m) => (
                  <Typography
                    key={`${m.label}-${m.value}`}
                    sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', mt: 0.2 }}
                  >
                    {m.label ? `${m.label}: ` : ''}
                    {m.value}
                  </Typography>
                ))}
                {fechaVencimiento ? (
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', mt: 0.2 }}>
                  Fecha de vencimiento: {fechaVencimiento}
                </Typography>
                ) : null}
              
                {ncf_factura_origen && (
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem' }}>
                    NCF Modificado: {ncf_factura_origen}
                  </Typography>
                )}
            </Box>
          </Grid>
        </Grid>

        {(parte || bloqueExtra) && (
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: 1.5,
              bgcolor: FP.color.surfaceAlt,
              border: `1px solid ${FP.color.border}`,
            }}
          >
            {parte && (
              <>
                <Typography sx={{ ...labelSx, color: FP.color.navy, mb: 1 }}>
                  {parte.titulo || 'Cliente'}
                </Typography>
                <Typography
                  sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.95rem', mb: 0.4 }}
                >
                  {parte.nombre || '—'}
                </Typography>
                {(parte.lineas || []).filter(Boolean).map((l) => (
                  <Typography key={l} sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                    {l}
                  </Typography>
                ))}
              </>
            )}
            {bloqueExtra && (bloqueExtra.lineas || []).filter(Boolean).length > 0 && (
              <Box sx={{ mt: parte ? 1.5 : 0, pt: parte ? 1.2 : 0, borderTop: parte ? `1px solid ${FP.color.border}` : 'none' }}>
                {bloqueExtra.titulo && (
                  <Typography sx={{ ...labelSx, color: FP.color.navy, mb: 0.6 }}>
                    {bloqueExtra.titulo}
                  </Typography>
                )}
                {(bloqueExtra.lineas || []).filter(Boolean).map((l) => (
                  <Typography key={l} sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                    {l}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}

        <Typography sx={{ ...labelSx, color: FP.color.navy, mb: 1 }}>Detalle</Typography>
        {varianteTabla === 'listado' ? (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
          >
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 640, sm: 720 } }}>
              <colgroup>
                {itemsAnchosListado.map((w, i) => (
                  <col key={i} style={{ width: w }} />
                ))}
              </colgroup>
              <TableBody>
                <TableRow
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    '& td': {
                      border: 'none',
                      borderBottom: `2px solid ${FP.color.border}`,
                      bgcolor: FP.color.surfaceAlt,
                    },
                  }}
                >
                  <TableCell>
                    <Typography component="span" sx={cellHeadListadoSx}>
                      Código
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={cellHeadListadoSx}>
                      Descripción
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadListadoSx, textAlign: 'center', width: '100%' }}>
                      Cant.
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadListadoSx, textAlign: 'right', width: '100%' }}>
                      Precio
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadListadoSx, textAlign: 'right', width: '100%' }}>
                      Desc.
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadListadoSx, textAlign: 'right', width: '100%' }}>
                      Subtotal
                    </Typography>
                  </TableCell>
                </TableRow>
                {!items || items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: FP.color.textSecondary, py: 4 }}>
                      Sin ítems
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, idx) => (
                    <TableRow key={item.id || idx} hover>
                      <TableCell
                        sx={{
                          ...cellBodyEllipsisSx,
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                        }}
                        title={item.codigo || '—'}
                      >
                        {item.codigo || '—'}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellBodyEllipsisSx,
                          fontWeight: 700,
                          color: FP.color.textPrimary,
                        }}
                        title={item.descripcion || '—'}
                      >
                        {item.descripcion || '—'}
                        {item.subdescripcion ? (
                          <Box
                            component="div"
                            sx={{ fontSize: '0.75rem', fontWeight: 400, color: FP.color.textSecondary }}
                          >
                            {item.subdescripcion}
                          </Box>
                        ) : null}
                      </TableCell>
                      <TableCell align="center">{parseFloat(item.cantidad || 0)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          ...cellBodyEllipsisSx,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {money(item.precio)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          ...cellBodyEllipsisSx,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {money(item.descuento)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          ...cellBodyEllipsisSx,
                          fontWeight: 700,
                          color: FP.color.textPrimary,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {money(item.subtotal)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <TableContainer
            sx={{ borderRadius: 1.5, border: `1px solid ${FP.color.border}`, overflow: 'hidden' }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={cellHeadSx}>Código</TableCell>
                  <TableCell sx={cellHeadSx}>Descripción</TableCell>
                  <TableCell align="center" sx={cellHeadSx}>
                    Cant.
                  </TableCell>
                  <TableCell align="right" sx={cellHeadSx}>
                    Precio
                  </TableCell>
                  <TableCell align="right" sx={cellHeadSx}>
                    Desc.
                  </TableCell>
                  <TableCell align="right" sx={cellHeadSx}>
                    Subtotal
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!items || items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: FP.color.textMuted, py: 3 }}>
                      Sin ítems
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, idx) => (
                    <TableRow
                      key={item.id || idx}
                      sx={{ bgcolor: idx % 2 === 0 ? '#FFFFFF' : FP.color.surfaceAlt }}
                    >
                      <TableCell sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                        {item.codigo || '—'}
                      </TableCell>
                      <TableCell
                        sx={{ color: FP.color.textPrimary, fontSize: '0.85rem', fontWeight: 600 }}
                      >
                        {item.descripcion || '—'}
                        {item.subdescripcion ? (
                          <Box
                            component="div"
                            sx={{ fontSize: '0.75rem', fontWeight: 400, color: FP.color.textSecondary }}
                          >
                            {item.subdescripcion}
                          </Box>
                        ) : null}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.85rem' }}>
                        {parseFloat(item.cantidad || 0)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {money(item.precio)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {money(item.descuento)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {money(item.subtotal)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 2,
            mt: 2.5,
            flexWrap: 'wrap',
          }}
        >
          {qrDataUrl ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <img src={qrDataUrl} alt="Código QR DGII" width={110} height={110} />
              {codigoSeguridad && (
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', mt: 0.6 }}>
                  Código Seg: {codigoSeguridad}
                </Typography>
              )}
              {fechaFirma && (
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', mt: 0.2 }}>
                  Fecha Firma: {fechaFirma}
                </Typography>
              )}
            </Box>
          ) : (
            <Box />
          )}
          <Box
            sx={{
              width: { xs: '100%', sm: 280 },
              ml: 'auto',
              border: `1px solid ${FP.color.border}`,
              borderRadius: 1.5,
              overflow: 'hidden',
            }}
          >
            {(filasTotales || []).map((f) => (
              <Box
                key={f.label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  px: 1.8,
                  py: 1,
                  borderBottom: `1px solid ${FP.color.border}`,
                }}
              >
                <Typography
                  sx={{
                    color: f.danger ? FP.color.danger : FP.color.textSecondary,
                    fontSize: '0.82rem',
                  }}
                >
                  {f.label}
                </Typography>
                <Typography
                  sx={{
                    color: f.danger ? FP.color.danger : FP.color.textPrimary,
                    fontSize: '0.82rem',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {typeof f.value === 'number' ? money(f.value) : f.value}
                </Typography>
              </Box>
            ))}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 1.8,
                py: 1.4,
                bgcolor: FP.color.navy,
              }}
            >
              <Typography
                sx={{ color: '#FFFFFF', fontWeight: 800, fontSize: '0.9rem', letterSpacing: '0.06em' }}
              >
                {totalLabel}
              </Typography>
              <Typography
                sx={{
                  color: '#FFFFFF',
                  fontWeight: 800,
                  fontSize: '1.15rem',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {typeof totalValue === 'number' ? money(totalValue) : totalValue}
              </Typography>
            </Box>
          </Box>
        </Box>

        {observaciones && (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ ...labelSx, color: FP.color.navy, mb: 0.8 }}>Observaciones</Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
              {observaciones}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            mt: 3,
            pt: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            textAlign: 'center',
          }}
        >
          {mensajeGracias && (
            <Typography sx={{ color: FP.color.navy, fontWeight: 700, fontSize: '0.85rem', mb: 0.4 }}>
              {mensajeGracias}
            </Typography>
          )}
          {footerNote && (
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.72rem' }}>{footerNote}</Typography>
          )}
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.7rem', mt: 0.3 }}>
            Emitido el {formatoFechaCorto(fecha)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default DocumentoEmpresarialPreview;
